import api from '../../config/axios';

// ============================================================================
// TEACHER SERVICE API
// All endpoints for teacher-related operations
// ============================================================================

export const teacherService = {
  // ============================================================================
  // DASHBOARD API
  // ============================================================================
  
  /**
   * Get teacher dashboard data
   * GET /api/teachers/dashboard
   * Purpose: Main dashboard information (stats, recent activity, etc.)
   * @returns {Promise} Response with dashboard data
   */
  getDashboard: async () => {
    const response = await api.get('/teachers/dashboard');
    return response.data;
  },

  // ============================================================================
  // STATISTICS API
  // ============================================================================

  // ============================================================================
  // COURSES MANAGEMENT API
  // ============================================================================
  
  /**
   * Get teacher lessons with parameters
   * GET /api/teachers/lessons
   * Purpose: List lessons with optional filtering/pagination
   * Note: Correct endpoint for lesson management
   * @param {string} params - Query parameters for filtering
   * @returns {Promise} Response with lessons data
   */
  getTeacherLessonsOriginal: async (params = '') => {
    const response = await api.get(`/teachers/lessons${params}`);
    return response.data;
  },

  /**
   * Get teacher lessons (simplified)
   * GET /api/teachers/lessons
   * Purpose: List all lessons for teacher for quiz binding selection
   * @param {Object} params - Optional query parameters
   * @returns {Promise} Response with lessons array
   */
  getTeacherLessons: async (params = {}) => {
    const response = await api.get('/teachers/lessons', { params });
    return response.data;
  },

  /**
   * Get teacher earnings
   * GET /api/teachers/earnings
   * Purpose: Get teacher earnings data with period filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} Response with earnings data
   */
  getTeacherEarnings: async (params) => {
    const response = await api.get('/teachers/earnings', { params });
    return response.data;
  },

  /**
   * Get teacher statistics with filters
   * GET /api/teachers/stats
   * Purpose: Detailed statistics with period and metric filters
   * @param {Object} params - Filter parameters (period, metric)
   * @returns {Promise} Response with filtered statistics
   */
  getTeacherStatistics: async (params = {}) => {
    // Use the existing /stats endpoint since /statistics doesn't exist
    const response = await api.get('/teachers/stats', { params });
    return response.data;
  },

  /**
   * Get teacher courses with flexible filtering
   * GET /api/teachers/courses
   * Purpose: List all courses for current teacher with search, pagination, and filters
   * @param {Object} params - Optional parameters {search, page, limit, status, etc.}
   * @returns {Promise} Response with courses array
   */
  getTeacherCourses: async (params = {}) => {
    const queryParams = new URLSearchParams();

    // Add all provided parameters to query string
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/teachers/courses?${queryString}` : '/teachers/courses';

    const response = await api.get(endpoint);
    return response.data;
  },

  /**
   * Get course videos
   * GET /api/teachers/courses/:courseId/videos
   * Purpose: List all videos in a specific course
   * @param {number} courseId - ID of the course
   * @returns {Promise} Response with videos array
   */
  getCourseVideos: async (courseId) => {
    const response = await api.get(`/teachers/courses/${courseId}/videos`);
    return response.data;
  },

  /**
   * Get signed file URL for a lesson (used for PDFs)
   * GET /api/lessons/:lessonId/file-url
   * @param {number} lessonId
   */
  getFileUrl: async (lessonId) => {
    const response = await api.get(`/lessons/${lessonId}/file-url`);
    return response.data;
  },

  /**
   * Delete course
   * DELETE /api/teachers/courses/:courseId
   * Purpose: Remove a course from the system
   * @param {number} courseId - ID of the course to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteCourse: async (courseId) => {
    const response = await api.delete(`/teachers/courses/${courseId}`);
    return response.data;
  },

  /**
   * Get single course details
   * GET /api/teachers/courses/:courseId
   * Purpose: Get detailed information about a specific course
   * @param {number} courseId - ID of the course
   * @returns {Promise} Response with course details
   */
  getTeacherCourse: async (courseId) => {
    const response = await api.get(`/teachers/courses/${courseId}`);
    return response.data;
  },

  // ============================================================================
  // STUDENTS API (Legacy - for management)
  // ============================================================================
  
  /**
   * Get students list for management
   * GET /api/teachers/students-list
   * Purpose: Detailed students list with filtering and pagination
   * Note: For student management (not messaging)
   * @param {Object} params - Parameters {search, teacherSubjectId, page, limit}
   * @returns {Promise} Response with paginated students data
   */
  /**
   * Get all students for messaging
   * GET /api/teachers/all-students
   * Purpose: Get list of all students for composing new message
   * @param {Object} params - Query parameters (search)
   * @returns {Promise} Response with students array
   */
  getStudentsList: async (params = {}) => {
    const { search = '', teacherSubjectId = '', page = 1, limit = 20 } = params;
    const queryParams = new URLSearchParams({
      ...(search && { search }),
      ...(teacherSubjectId && { teacherSubjectId }),
      page: page.toString(),
      limit: limit.toString()
    });
    // Use the new all-students endpoint for composing messages
    const response = await api.get(`/teachers/all-students?${queryParams}`);
    return response.data;
  },

  // ============================================================================
  // SUBJECTS API
  // ============================================================================
  
  /**
   * Get subjects
   * GET /api/teachers/subjects
   * Purpose: List subjects taught by teacher
   * @returns {Promise} Response with subjects array
   */
  getSubjects: async () => {
    const response = await api.get('/teachers/subjects');
    return response.data;
  },

  // ============================================================================
  // MESSAGES API
  // ============================================================================
  
  /**
   * Get teacher messages
   * GET /api/teachers/messages
   * Purpose: List all messages received by teacher
   * @returns {Promise} Response with messages array
   */
  getMessages: async () => {
    const response = await api.get('/teachers/messages');
    return response.data;
  },

  /**
   * Get message threads (conversations grouped by user)
   * GET /api/teachers/messages/threads
   * Purpose: Get all conversations as thread previews
   * @returns {Promise} Response with threads array
   */
  getMessageThreads: async () => {
    const response = await api.get('/teachers/messages/threads');
    return response.data;
  },

  /**
   * Get conversation thread with specific user
   * GET /api/teachers/messages/thread/:userId
   * Purpose: Get all messages in conversation with specific user
   * @param {number} userId - ID of the other user
   * @returns {Promise} Response with messages in conversation
   */
  getConversationThread: async (userId) => {
    const response = await api.get(`/teachers/messages/thread/${userId}`);
    return response.data;
  },

  /**
   * Send new message
   * POST /api/teachers/messages
   * Purpose: Send a new message to a student
   * @param {Object} messageData - Message content and recipient info
   * @returns {Promise} Response with sent message data
   */
  sendMessage: async (messageData) => {
    const response = await api.post('/teachers/messages', messageData);
    return response.data;
  },

  /**
   * Reply to message
   * POST /api/teachers/messages/:messageId/reply
   * Purpose: Reply to a specific message
   * @param {number} messageId - ID of message to reply to
   * @param {Object} messageData - Reply content
   * @returns {Promise} Response with reply data
   */
  replyToMessage: async (messageId, messageData) => {
    const response = await api.post(`/teachers/messages/${messageId}/reply`, messageData);
    return response.data;
  },

  /**
   * Delete message
   * DELETE /api/teachers/messages/:messageId
   * Purpose: Remove a message from the system
   * @param {number} messageId - ID of message to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteMessage: async (messageId) => {
    const response = await api.delete(`/teachers/messages/${messageId}`);
    return response.data;
  },

  /**
   * Mark a message as read
   * PUT /api/teachers/messages/:messageId/read
   * Purpose: Mark a message as read
   * @param {number} messageId - ID of the message
   * @returns {Promise} Response with updated message
   */
  markMessageAsRead: async (messageId) => {
    const response = await api.put(`/teachers/messages/${messageId}/read`);
    return response.data;
  },

  /**
   * Star a message
   * PUT /api/teachers/messages/:messageId/star
   * Purpose: Mark a message as starred/important
   * @param {number} messageId - ID of the message
   * @returns {Promise} Response with updated message
   */
  starMessage: async (messageId) => {
    const response = await api.put(`/teachers/messages/${messageId}/star`);
    return response.data;
  },

  /**
   * Unstar a message
   * DELETE /api/teachers/messages/:messageId/star
   * Purpose: Remove star from a message
   * @param {number} messageId - ID of the message
   * @returns {Promise} Response with updated message
   */
  unstarMessage: async (messageId) => {
    const response = await api.delete(`/teachers/messages/${messageId}/star`);
    return response.data;
  },

  /**
   * Get starred messages
   * GET /api/teachers/messages/starred
   * Purpose: Get all starred/important messages
   * @returns {Promise} Response with array of starred messages
   */
  getStarredMessages: async () => {
    const response = await api.get('/teachers/messages/starred');
    return response.data;
  },

  /**
   * Update typing status
   * PUT /api/teachers/typing-status
   * Purpose: Indicate that user is typing
   * @param {number} conversationUserId - ID of the other user in conversation
   * @param {boolean} isTyping - Whether user is currently typing
   * @returns {Promise} Response with status update
   */
  updateTypingStatus: async (conversationUserId, isTyping) => {
    const response = await api.put('/teachers/typing-status', {
      conversationUserId,
      isTyping
    });
    return response.data;
  },

  /**
   * Get typing status for a conversation
   * GET /api/teachers/typing-status/:conversationUserId
   * Purpose: Check which users are typing in a conversation
   * @param {number} conversationUserId - ID of the other user
   * @returns {Promise} Response with list of users typing
   */
  getTypingStatus: async (conversationUserId) => {
    const response = await api.get(`/teachers/typing-status/${conversationUserId}`);
    return response.data;
  },

  /**
   * Clear typing status
   * DELETE /api/teachers/typing-status
   * Purpose: Clear all typing statuses for user (on disconnect)
   * @returns {Promise} Response confirming clear
   */
  clearTypingStatus: async () => {
    const response = await api.delete('/teachers/typing-status');
    return response.data;
  },

  // ============================================================================
  // PUBLIC PROFILE API
  // ============================================================================
  
  /**
   * Get teacher public profile
   * GET /api/public/teachers/:teacherId/profile
   * Purpose: Display teacher profile to public/students
   * @param {number} teacherId - ID of the teacher
   * @returns {Promise} Response with teacher profile data
   */
  getTeacherProfile: async (teacherId) => {
    const response = await api.get(`/public/teachers/${teacherId}/profile`);
    return response.data;
  },

  /**
   * Get teacher public courses
   * GET /api/courses/public?teacherId=:teacherId
   * Purpose: Display teacher's courses to public
   * @param {number} teacherId - ID of the teacher
   * @returns {Promise} Response with public courses array
   */
  getPublicTeacherCourses: async (teacherId) => {
    const response = await api.get(`/courses/public?teacherId=${teacherId}`);
    return response.data;
  },

  // ============================================================================
  // TEACHER QUESTIONS API
  // ============================================================================
  
  /**
   * Get teacher questions
   * GET /api/teachers/questions
   * Purpose: List questions asked to teacher
   * @param {Object} params - Query parameters (answered, lessonId, courseId, page, limit)
   * @returns {Promise} Response with questions array
   */
  getTeacherQuestions: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/teachers/questions?${queryParams}`);
    return response.data;
  },

  // Answer Question
  answerQuestion: async (questionId, data) => {
    const response = await api.put(`/teachers/questions/${questionId}/answer`, data);
    return response.data;
  },

  // ============================================================================
  // TEACHER INFO API
  // ============================================================================
  
  /**
   * Get teacher info
   * GET /api/teachers/info
   * Purpose: Get teacher's profile information
   * @returns {Promise} Response with teacher info
   */
  getTeacherInfo: async () => {
    const response = await api.get('/teachers/info');
    return response.data;
  },

  // ============================================================================
  // TEACHER STUDENTS API
  // ============================================================================
  
  /**
   * Get students list for management
   * GET /api/teachers/students
   * Purpose: List students with filtering and pagination
   * @param {Object} params - Query parameters (teacherSubjectId, search, page, limit)
   * @returns {Promise} Response with students array
   */
  getStudents: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/teachers/students?${queryParams}`);
    return response.data;
  },

  // ============================================================================
  // COURSES API
  // ============================================================================
  
  /**
   * Get single course details
   * GET /api/teachers/courses/:courseId
   * Purpose: Full course information for editing/viewing
   * @param {number} courseId - ID of the course
   * @returns {Promise} Response with course details
   */
  getCourse: async (courseId) => {
    const response = await api.get(`/teachers/courses/${courseId}`);
    return response.data;
  },

  /**
   * Create new course
   * POST /api/teachers/courses
   * Purpose: Add new course to the system
   * @param {Object} courseData - Course information
   * @returns {Promise} Response with created course
   */
  createCourse: async (courseData) => {
    const response = await api.post('/teachers/courses', courseData);
    return response.data;
  },

  /**
   * Update existing course
   * PUT /api/teachers/courses/:courseId
   * Purpose: Modify course information
   * @param {number} courseId - ID of the course to update
   * @param {Object} courseData - Updated course information
   * @returns {Promise} Response with updated course
   */
  updateCourse: async (courseId, courseData) => {
    const response = await api.put(`/teachers/courses/${courseId}`, courseData);
    return response.data;
  },

  // ============================================================================
  // COURSE VIDEOS API
  // ============================================================================
  
  /**
   * Get all videos for a course
   * GET /api/teachers/courses/:courseId/videos
   * Purpose: List videos in course management
   * @param {number} courseId - ID of the course
   * @returns {Promise} Response with videos array
   */
  /**
   * Delete video from course
   * DELETE /api/teachers/courses/:courseId/videos/:videoId
   * Purpose: Remove specific video from course
   * @param {number} courseId - ID of the course
   * @param {number} videoId - ID of the video to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteCourseVideo: async (courseId, videoId) => {
    const response = await api.delete(`/teachers/courses/${courseId}/videos/${videoId}`);
    return response.data;
  },

  // ============================================================================
  // LESSONS API
  // ============================================================================
  
  /**
   * Get single lesson details
   * GET /api/teachers/lessons/:lessonId
   * Purpose: Full lesson information for editing/viewing
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with lesson details
   */
  getLesson: async (lessonId) => {
    const response = await api.get(`/teachers/lessons/${lessonId}`);
    return response.data;
  },

  /**
   * Create new lesson
   * POST /api/teachers/lessons
   * Purpose: Add new lesson to system
   * @param {Object} lessonData - Lesson information
   * @returns {Promise} Response with created lesson
   */
  createLesson: async (lessonData) => {
    const response = await api.post('/teachers/lessons', lessonData);
    return response.data;
  },

  /**
   * Update existing lesson
   * PUT /api/teachers/lessons/:lessonId
   * Purpose: Modify lesson information
   * @param {number} lessonId - ID of the lesson to update
   * @param {Object} lessonData - Updated lesson information
   * @returns {Promise} Response with updated lesson
   */
  updateLesson: async (lessonId, lessonData) => {
    const response = await api.put(`/teachers/lessons/${lessonId}`, lessonData);
    return response.data;
  },

  /**
   * Delete lesson
   * DELETE /api/teachers/lessons/:lessonId
   * Purpose: Remove lesson from system
   * @param {number} lessonId - ID of the lesson to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteLesson: async (lessonId) => {
    const response = await api.delete(`/teachers/lessons/${lessonId}`);
    return response.data;
  },

  // ============================================================================
  // REPORTS API
  // ============================================================================
  
  /**
   * Get teacher reports
   * GET /api/teachers/reports
   * Purpose: Get teacher performance and activity reports
   * @param {Object} params - Query parameters (period, type)
   * @returns {Promise} Response with reports data
   */
  getTeacherReports: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/teachers/reports?${queryParams}`);
    return response.data;
  },

  // ============================================================================
  // EARNINGS API
  // ============================================================================
  
  /**
   * Get teacher earnings data
   * GET /api/teachers/earnings
   * Purpose: Financial reports and earnings analytics
   * @param {string} period - Time period (month, week, year)
   * @param {number} year - Year for filtering
   * @returns {Promise} Response with earnings data
   */
  getEarnings: async (period = 'month', year = new Date().getFullYear()) => {
    const response = await api.get(`/teachers/earnings?period=${period}&year=${year}`);
    return response.data;
  },

  // ============================================================================
  // SCHEDULE MANAGEMENT API
  // ============================================================================
  
  /**
   * Get teacher schedule
   * GET /api/teachers/schedule
   * Purpose: Get teacher's class schedule
   * @param {Object} params - Query parameters (week, etc.)
   * @returns {Promise} Response with schedule data
   */
  getTeacherSchedule: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/teachers/schedule?${queryParams}`);
    return response.data;
  },

  /**
   * Get available time slots for scheduling
   * GET /api/teachers/schedule/available-slots
   * Purpose: Get available time slots for scheduling
   * @param {Object} params - Query parameters (dayOfWeek, etc.)
   * @returns {Promise} Response with available slots
   */
  getAvailableSlots: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/teachers/schedule/available-slots?${queryParams}`);
    return response.data;
  },

  /**
   * Create scheduled lesson
   * POST /api/teachers/schedule
   * Purpose: Create new scheduled lesson
   * @param {Object} scheduleData - Schedule details (courseId, lessonTitle, etc.)
   * @returns {Promise} Response with created schedule
   */
  createSchedule: async (scheduleData) => {
    const response = await api.post('/teachers/schedule', scheduleData);
    return response.data;
  },

  /**
   * Update scheduled lesson
   * PUT /api/teachers/schedule/:scheduleId
   * Purpose: Update existing scheduled lesson
   * @param {number} scheduleId - ID of the schedule to update
   * @param {Object} data - Updated schedule data
   * @returns {Promise} Response with updated schedule
   */
  updateSchedule: async (scheduleId, data) => {
    const response = await api.put(`/teachers/schedule/${scheduleId}`, data);
    return response.data;
  },

  /**
   * Delete scheduled lesson
   * DELETE /api/teachers/schedule/:scheduleId
   * Purpose: Delete a scheduled lesson
   * @param {number} scheduleId - ID of the schedule to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteSchedule: async (scheduleId) => {
    const response = await api.delete(`/teachers/schedule/${scheduleId}`);
    return response.data;
  },

  // ============================================================================
  // LESSON CREATION API
  // ============================================================================
  
  /**
   * Create lesson with external video
   * POST /api/teachers/lessons/video-external
   * Purpose: Create lesson with external video URL
   * @param {FormData} lessonData - Lesson data with thumbnail and video URL
   * @returns {Promise} Response with created lesson
   */
  createVideoLessonExternal: async (lessonData) => {
    const response = await api.post('/teachers/lessons/video-external', lessonData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Create lesson with file upload
   * POST /api/teachers/lessons/file
   * Purpose: Create lesson with file (PDF, Word, Excel, etc.)
   * @param {FormData} lessonData - Lesson data with file and thumbnail
   * @returns {Promise} Response with created lesson
   */
  createFileLesson: async (lessonData) => {
    const response = await api.post('/teachers/lessons/file', lessonData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Create lesson with video file upload
   * POST /api/teachers/lessons/video-upload
   * Purpose: Create lesson and upload video file
   * @param {FormData} lessonData - Lesson data with video file and thumbnail
   * @returns {Promise} Response with created lesson
   */
  createVideoLessonUpload: async (lessonData) => {
    const response = await api.post('/teachers/lessons/video-upload', lessonData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // ============================================================================
  // COURSE VIDEO MANAGEMENT API
  // ============================================================================
  
  /**
   * Add external video to course
   * POST /api/teachers/courses/:courseId/add-external-video
   * Purpose: Add external video to existing course
   * @param {number} courseId - ID of the course
   * @param {Object} videoData - External video URL and metadata
   * @returns {Promise} Response with added video
   */
  addExternalVideoToCourse: async (courseId, videoData) => {
    const response = await api.post(`/teachers/courses/${courseId}/add-external-video`, videoData);
    return response.data;
  },

  /**
   * Update course video
   * PUT /api/teachers/courses/:courseId/videos/:videoId
   * Purpose: Update existing video in course
   * @param {number} courseId - ID of the course
   * @param {number} videoId - ID of the video to update
   * @param {Object} updateData - Updated video information
   * @returns {Promise} Response with updated video
   */
  updateCourseVideo: async (courseId, videoId, updateData) => {
    const response = await api.put(`/teachers/courses/${courseId}/videos/${videoId}`, updateData);
    return response.data;
  },

  /**
   * Get lesson video URL (Signed URL)
   * GET /api/teachers/lessons/:lessonId/video-url
   * Purpose: Get temporary URL for video access
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with signed URL
   */
  getLessonVideoUrl: async (lessonId) => {
    const response = await api.get(`/teachers/lessons/${lessonId}/video-url`);
    return response.data;
  },

  /**
   * Get lesson thumbnail image URL (Secure URL with token)
   * GET /api/lessons/:lessonId/image-url
   * Purpose: Get secure URL for lesson thumbnail from Bunny CDN
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with secure image URL
   */
  getLessonImageUrl: async (lessonId) => {
    const response = await api.get(`/lessons/${lessonId}/image-url`);
    return response.data;
  },

  /**
   * Get course thumbnail image URL (Secure URL with token)
   * GET /api/lessons/course/:courseId/image-url
   * Purpose: Get secure URL for course thumbnail from Bunny CDN
   * @param {number} courseId - ID of the course
   * @returns {Promise} Response with secure image URL
   */
  getCourseImageUrl: async (courseId) => {
    const response = await api.get(`/lessons/course/${courseId}/image-url`);
    return response.data;
  },

  /**
   * Delete entire conversation with a student
   * DELETE /api/teachers/conversations/:userId
   * Purpose: Delete all messages in conversation with specific student
   * @param {number} userId - ID of the student
   * @returns {Promise} Response with deletion result
   */
  deleteConversation: async (userId) => {
    const response = await api.delete(`/teachers/conversations/${userId}`);
    return response.data;
  },

  // ============================================================================
  // QUIZ MANAGEMENT API
  // ============================================================================

  /**
   * Create new quiz
   * POST /api/teachers/quizzes
   * Purpose: Create a new quiz with questions
   * @param {Object} quizData - Quiz information with questions
   * @returns {Promise} Response with created quiz
   */
  createQuiz: async (quizData) => {
    const response = await api.post('/teachers/quizzes', quizData);
    return response.data;
  },

  /**
   * Get teacher quizzes
   * GET /api/teachers/quizzes
   * Purpose: List all quizzes created by teacher
   * @returns {Promise} Response with quizzes list
   */
  getTeacherQuizzes: async () => {
    const response = await api.get('/teachers/quizzes');
    return response.data;
  },

  /**
   * Get quiz details
   * GET /api/teachers/quizzes/:quizId
   * Purpose: Get detailed information about a quiz
   * @param {number} quizId - ID of the quiz
   * @returns {Promise} Response with quiz details
   */
  getQuizDetails: async (quizId) => {
    const response = await api.get(`/teachers/quizzes/${quizId}`);
    return response.data;
  },

  /**
   * Update quiz
   * PUT /api/teachers/quizzes/:quizId
   * Purpose: Update quiz information
   * @param {number} quizId - ID of the quiz
   * @param {Object} updateData - Updated quiz data
   * @returns {Promise} Response with updated quiz
   */
  updateQuiz: async (quizId, updateData) => {
    const response = await api.put(`/teachers/quizzes/${quizId}`, updateData);
    return response.data;
  },

  /**
   * Delete quiz
   * DELETE /api/teachers/quizzes/:quizId
   * Purpose: Delete a quiz
   * @param {number} quizId - ID of the quiz
   * @returns {Promise} Response with deletion result
   */
  deleteQuiz: async (quizId) => {
    const response = await api.delete(`/teachers/quizzes/${quizId}`);
    return response.data;
  },

  /**
   * Get lesson quizzes
   * GET /api/teachers/lessons/:lessonId/quizzes
   * Purpose: Get all quizzes linked to a lesson
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with linked quizzes
   */
  getLessonQuizzes: async (lessonId) => {
    const response = await api.get(`/teachers/lessons/${lessonId}/quizzes`);
    return response.data;
  },

  /**
   * Get quiz attempt review (student)
   * GET /api/quizzes/:quizId/attempts/:attemptId/review
   * Purpose: Get detailed review of quiz attempt with correct answers
   * @param {number} quizId - ID of the quiz
   * @param {number} attemptId - ID of the attempt
   * @returns {Promise} Response with attempt review
   */
  getAttemptReview: async (quizId, attemptId) => {
    const response = await api.get(`/quizzes/${quizId}/attempts/${attemptId}/review`);
    return response.data;
  },

  /**
   * Get quiz attempts results
   * GET /api/quizzes/:quizId/attempts
   * Purpose: Get all attempts and results for a specific quiz
   * @param {number} quizId - ID of the quiz
   * @returns {Promise} Response with attempts array
   */
  getQuizAttempts: async (quizId) => {
    const response = await api.get(`/quizzes/${quizId}/attempts`);
    return response.data;
  }
};

export default teacherService;

